package com.eladfinish.bible.contest.screens.lexicographicalSort.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.keter_medium
import com.eladfinish.bible.contest.screens.lexicographicalSort.model.LexicographicalTable
import com.eladfinish.bible.contest.screens.lexicographicalSort.model.LexicographicalTableLabels
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import dev.icerock.moko.resources.compose.fontFamilyResource

/**
 * The lexicographical-sort table drawn with Compose, for the platform that has no WebView to show
 * `table.html` in. It follows that page's layout: same title, same columns, same Keter font.
 */
@Composable
fun LexicographicalTableView(
    table: LexicographicalTable,
    modifier: Modifier = Modifier,
) {
    val listState = rememberLazyListState()
    val bodyStyle = TextStyle(
        fontFamily = fontFamilyResource(SharedRes.fonts.keter_medium),
        fontSize = 18.sp,
        color = Color.Black,
    )
    val smallStyle = bodyStyle.copy(fontSize = 14.sp)

    // The table is Hebrew whatever the app language is, like the `dir="rtl"` of the HTML page.
    CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Rtl) {
        Box(
            modifier = modifier.background(Color.White),
            contentAlignment = Alignment.TopCenter,
        ) {
            SelectionContainer {
                LazyColumn(
                    state = listState,
                    modifier = Modifier
                        .widthIn(max = TableMaxWidth)
                        .fillMaxHeight()
                        .mouseVerticalScrollbar(listState),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
                ) {
                    item {
                        Text(
                            text = LexicographicalTableLabels.TITLE_FORMAT.formatString(table.place),
                            modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                            style = bodyStyle.copy(fontSize = 28.sp, fontWeight = FontWeight.Bold),
                            textAlign = TextAlign.Center,
                        )
                    }
                    if (table.verseCount > 0) {
                        item {
                            Text(
                                text = LexicographicalTableLabels.verseCount(table.verseCount),
                                modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                                style = bodyStyle.copy(fontSize = 20.sp),
                                textAlign = TextAlign.Center,
                            )
                        }
                    }
                    item {
                        TableRow {
                            val headerStyle = smallStyle.copy(fontWeight = FontWeight.Bold)
                            Cell(LexicographicalTableLabels.CHECKBOX_HEADER, headerStyle, CheckboxWidth, bordered = false)
                            Cell(LexicographicalTableLabels.NUMBER, headerStyle, NumberWidth)
                            TextCell(LexicographicalTableLabels.REYSHA, bodyStyle.copy(fontWeight = FontWeight.Bold), TextAlign.Start)
                            TextCell(LexicographicalTableLabels.SEYFA, bodyStyle.copy(fontWeight = FontWeight.Bold), TextAlign.End)
                            table.speakerTitle?.let { Cell(it, headerStyle, SpeakerWidth) }
                            Cell(LexicographicalTableLabels.CHAPTER, headerStyle, ReferenceWidth)
                            Cell(LexicographicalTableLabels.VERSE, headerStyle, ReferenceWidth)
                        }
                    }
                    items(table.rows) { row ->
                        when (row) {
                            is LexicographicalTable.Row.Verse -> TableRow {
                                Cell(LexicographicalTableLabels.CHECKBOX, smallStyle, CheckboxWidth, bordered = false)
                                Cell(row.number.toString(), smallStyle, NumberWidth)
                                TextCell(row.reysha, bodyStyle, TextAlign.Start)
                                TextCell(row.seyfa, bodyStyle, TextAlign.End)
                                if (table.speakerTitle != null) Cell(row.speaker.orEmpty(), smallStyle, SpeakerWidth)
                                Cell(row.chapter, smallStyle, ReferenceWidth)
                                Cell(row.verse, smallStyle, ReferenceWidth)
                            }

                            is LexicographicalTable.Row.Teaser -> Text(
                                text = "⋮\n${row.text}\n⋮",
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(TeaserBackground)
                                    .border(BorderWidth, Color.Black)
                                    .padding(horizontal = 8.dp, vertical = 14.dp),
                                style = bodyStyle.copy(color = TeaserText, lineHeight = 30.sp),
                                textAlign = TextAlign.Center,
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TableRow(content: @Composable RowScope.() -> Unit) {
    // Every cell is as tall as the tallest one, so the borders form a grid.
    Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min), content = content)
}

/** A fixed-width, centred cell: the narrow columns. */
@Composable
private fun Cell(text: String, style: TextStyle, width: Dp, bordered: Boolean = true) {
    Box(
        modifier = Modifier
            .width(width)
            .fillMaxHeight()
            .then(if (bordered) Modifier.border(BorderWidth, Color.Black) else Modifier)
            .padding(horizontal = 2.dp, vertical = 4.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(text = text, style = style, textAlign = TextAlign.Center)
    }
}

/** One of the two verse-text columns, which share the width the narrow columns leave. */
@Composable
private fun RowScope.TextCell(text: String, style: TextStyle, textAlign: TextAlign) {
    Text(
        text = text,
        modifier = Modifier
            .weight(1f)
            .fillMaxHeight()
            .border(BorderWidth, Color.Black)
            .padding(horizontal = 8.dp, vertical = 4.dp),
        style = style,
        textAlign = textAlign,
    )
}

private val TableMaxWidth = 900.dp
private val CheckboxWidth = 32.dp
private val NumberWidth = 48.dp
private val SpeakerWidth = 80.dp
private val ReferenceWidth = 48.dp
private val BorderWidth = 0.5.dp
private val TeaserBackground = Color(0xFFD7CCC8)
private val TeaserText = Color(0xFF5D4037)

@Preview
@Composable
private fun LexicographicalTableViewPreview() {
    PreviewTheme {
        LexicographicalTableView(
            table = LexicographicalTable(
                place = "איוב",
                verseCount = 1070,
                speakerTitle = "דובר",
                rows = listOf(
                    LexicographicalTable.Row.Verse(1, "אָ֭ב לָאֶבְי֑וֹן", "אֶחְקְרֵֽהוּ׃", "איוב", "כט", "טז"),
                    LexicographicalTable.Row.Teaser("ועוד 1,050 פסוקים בטבלה המלאה"),
                    LexicographicalTable.Row.Verse(1070, "תַּ֭חַת חִטָּ֨ה", "תַּ֝֗מּוּ דִּבְרֵ֥י אִיּֽוֹב׃", "איוב", "לא", "מ"),
                ),
            ),
        )
    }
}
